import { useRef, useEffect, useState, useMemo, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Button,
  ContentCard,
  Drawer,
  LoadingSkeleton,
  Row,
  Tabs,
  TruncatedAccount,
} from "@narmi/design_system";
import { useSetChatVisibility } from "cerulean";
import {
  formatBulkTemplate,
  useGetTemplate,
  useGetTemplateVersions,
  DisplayStatus,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useACHCompany } from "byzantine/src/dbbl/businessLogic/entities/achCompanies";
import {
  useBulkTemplatePermissions,
  isMakePaymentDisabled,
} from "byzantine/src/dbbl/businessLogic/modules/bulkTemplates";
import Filters from "byzantine/src/filters";
import { formatPaymentTotal } from "../../../PaymentActivity/CreateBulk/templates/templateHelpers";
import { StatusTag } from "../../../PaymentActivity/common";
import { PAYMENT_ACTIVITY_ROUTE } from "../../../PaymentActivity/routes";
import activityStyles from "../../../PaymentActivity/PaymentActivity.module.scss";
import ViewPaymentsDialog from "./ViewPaymentsDialog";
import VersionHistoryTab from "./VersionHistoryTab";
import styles from "./Drawer.module.scss";

export type BulkTemplateDrawerProps = {
  isOpen: boolean;
  template: FormattedBulkTemplate | null;
  handleClose: () => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  templateUuid: API.BulkTemplateId;
  onArchive: (template: FormattedBulkTemplate) => void;
  onEdit: (
    template: FormattedBulkTemplate,
    versionId?: API.BulkTemplateVersionId
  ) => void;
  editLoading?: boolean;
};

type TemplateState = FormattedBulkTemplate["state"];

const getDrawerActions = (
  state: TemplateState
): {
  editEnabled: boolean;
  archiveEnabled: boolean;
  makePaymentEnabled: boolean;
} => {
  switch (state) {
    case "active":
      return {
        editEnabled: true,
        archiveEnabled: true,
        makePaymentEnabled: true,
      };
    case "active_pending":
      return {
        editEnabled: false,
        archiveEnabled: true,
        makePaymentEnabled: true,
      };
    case "rejected":
      return {
        editEnabled: true,
        archiveEnabled: true,
        makePaymentEnabled: false,
      };
    case "pending_approval":
      return {
        editEnabled: false,
        archiveEnabled: true,
        makePaymentEnabled: false,
      };
    default:
      return {
        editEnabled: false,
        archiveEnabled: false,
        makePaymentEnabled: false,
      };
  }
};

const BulkTemplateDrawer = ({
  isOpen,
  template,
  handleClose,
  handleOnNext,
  handleOnPrev,
  templateUuid,
  onArchive,
  onEdit,
  editLoading,
}: BulkTemplateDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);

  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { getTemplate, loading } = useGetTemplate();
  const { getTemplateVersions, loading: versionsLoading } =
    useGetTemplateVersions();
  const accounts = useAllAccounts();
  const achCompany = useACHCompany();
  const permissions = useBulkTemplatePermissions();

  const [fetchedTemplate, setFetchedTemplate] =
    useState<API.BulkTemplate | null>(null);
  const [versions, setVersions] = useState<API.BulkTemplateVersion[]>([]);
  const [isPaymentsDialogOpen, setIsPaymentsDialogOpen] = useState(false);

  // fetch template data only when templateUuid changes (deep-link / refresh)
  // or when template prop doesnt exist
  useEffect(() => {
    if (!template && templateUuid) {
      getTemplate(templateUuid, {
        onData: (response) => {
          setFetchedTemplate(response);
        },
      });
    }
  }, [template, templateUuid, getTemplate]);

  // fetch versions list for the template — used by VersionHistoryTab
  // and as a fallback for rejected/archived templates with no active/pending version
  useEffect(() => {
    if (templateUuid) {
      setVersions([]);
      getTemplateVersions(templateUuid, {
        onData: (data) => setVersions(data),
      });
    }
  }, [templateUuid, getTemplateVersions]);

  const fallbackVersion = versions.length > 0 ? versions[0] : null;
  const formattedBulkTemplate = useMemo(() => {
    if (template) {
      // if template has no version data but we have a fallback version, re-format
      if (template.versionNumber === null && fallbackVersion) {
        return formatBulkTemplate(
          {
            id: template.uuid,
            name: template.name,
            state: template.state,
            last_used: template.lastUsed,
            created_at: "",
            updated_at: template.updatedAt,
            archived_at: null,
            active_version: fallbackVersion,
            pending_version: null,
          },
          accounts
        );
      }
      return template;
    }
    // if fetchedTemplate has no version data but we have a fallback version, re-format
    if (fetchedTemplate) {
      if (
        !fetchedTemplate.active_version &&
        !fetchedTemplate.pending_version &&
        fallbackVersion
      ) {
        return formatBulkTemplate(
          { ...fetchedTemplate, active_version: fallbackVersion },
          accounts
        );
      }
      return formatBulkTemplate(fetchedTemplate, accounts);
    }
    return null;
  }, [template, fetchedTemplate, accounts, fallbackVersion]);

  const drawerActions = formattedBulkTemplate
    ? getDrawerActions(formattedBulkTemplate.state)
    : { editEnabled: false, archiveEnabled: false, makePaymentEnabled: false };

  const editDisabled =
    !drawerActions.editEnabled || !permissions.canEdit || Boolean(editLoading);

  const renderDrawerContent = () => {
    if (!formattedBulkTemplate) return null;

    const {
      name,
      displayStatus,
      paymentCount,
      paymentCreditCount,
      paymentDebitCount,
      creditTotalDollars,
      debitTotalDollars,
      versionNumber,
      institutionAccount,
      secCode,
      entryDesc,
      description,
      maxExpediteChoice,
      groupPosting,
      lastUsed,
      pendingDualApprovalId,
    } = formattedBulkTemplate;

    const isPendingApproval = displayStatus === DisplayStatus.pending_approval;

    const paymentDetailsRows: Array<
      [string | ReactElement<any>, string | undefined | ReactElement<any>]
    > = [];

    if (paymentCreditCount > 0) {
      paymentDetailsRows.push([
        l10n.getString("label-sending"),
        formatPaymentTotal(l10n, creditTotalDollars, paymentCreditCount),
      ]);
    }

    if (paymentDebitCount > 0) {
      paymentDetailsRows.push([
        l10n.getString("label-collecting"),
        formatPaymentTotal(l10n, debitTotalDollars, paymentDebitCount),
      ]);
    }

    if (paymentCount > 0 && !isPendingApproval) {
      paymentDetailsRows.push([
        l10n.getString("label-template-details"),
        <Button
          kind="plain"
          size="s"
          onClick={() => setIsPaymentsDialogOpen(true)}
        >
          {l10n.getString("view-payments-cta", { total: paymentCount })}
          <span className="narmi-icon-chevron-right" />
        </Button>,
      ]);
    }

    if (isPendingApproval && pendingDualApprovalId) {
      paymentDetailsRows.push([
        <Button
          kind="plain"
          size="s"
          onClick={() => {
            navigate(
              `/approvals/templates?dualApprovalId=${pendingDualApprovalId}`
            );
          }}
        >
          {l10n.getString("label-view-approvals")}
          <span className="narmi-icon-chevron-right" />
        </Button>,
        undefined,
      ]);
    }

    const templateDetailsRows: Array<
      [string, string | undefined | ReactElement<any>]
    > = [];

    templateDetailsRows.push([
      l10n.getString("label-version"),
      versionNumber != null ? `V${versionNumber}` : undefined,
    ]);

    if (institutionAccount) {
      templateDetailsRows.push([
        l10n.getString("label-originating-account"),
        <TruncatedAccount
          name={institutionAccount.name ?? ""}
          lastFour={institutionAccount.getMaskedNumber() ?? ""}
        />,
      ]);
    }

    templateDetailsRows.push([
      l10n.getString("speed-label"),
      maxExpediteChoice > 0
        ? l10n.getString("label-allowed-to-expedite")
        : l10n.getString("label-not-allowed-to-expedite"),
    ]);

    if (secCode) {
      templateDetailsRows.push([l10n.getString("label-payment-type"), secCode]);
    }

    if (entryDesc) {
      templateDetailsRows.push([
        l10n.getString("label-purpose-payment"),
        entryDesc,
      ]);
    }

    if (description) {
      templateDetailsRows.push([
        l10n.getString("label-note-internal"),
        description,
      ]);
    }

    if (achCompany?.company_id) {
      templateDetailsRows.push([
        l10n.getString("label-company-name-id"),
        `${achCompany.company_name}/${achCompany.company_id}`,
      ]);
    }

    if (lastUsed) {
      templateDetailsRows.push([
        l10n.getString("label-last-used"),
        <Button
          kind="plain"
          size="s"
          onClick={() =>
            navigate(
              `/${PAYMENT_ACTIVITY_ROUTE}/bulk?template=${formattedBulkTemplate.uuid}`
            )
          }
        >
          {Filters.longMonthDayYear(lastUsed)}
        </Button>,
      ]);
    }

    return (
      <>
        <div className="margin--bottom--s">
          <StatusTag status={displayStatus} />
        </div>
        <h1 className={`${styles.drawerTitle}`}>{name}</h1>
        <div className="margin--top--l">
          <Tabs defaultSelectedIndex={0}>
            <Tabs.List>
              <Tabs.Tab label={l10n.getString("tab-details")} tabId="details" />
              <Tabs.Tab
                label={l10n.getString("tab-version-history")}
                tabId="version-history"
              />
            </Tabs.List>
            <Tabs.Panel tabId="details">
              <div className="margin--top--l">
                <ContentCard kind="bordered" paddingSize="l">
                  <h5 className="margin--bottom--s fontSize--xs fontWeight--bold fontColor--primary">
                    {l10n
                      .getString("ach-payment-payment-details-title")
                      .toUpperCase()}
                  </h5>
                  {paymentDetailsRows
                    .filter(
                      ([heading, content]) =>
                        Boolean(heading) || Boolean(content)
                    )
                    .map(([heading, content], index, filteredRows) => (
                      <div
                        key={`payment-row-${heading}`}
                        className={
                          index !== filteredRows.length - 1
                            ? "margin--bottom--l"
                            : ""
                        }
                      >
                        <Row alignItems="center">
                          <Row.Item shrink>
                            <span className="fontSize--s fontWeight--semibold fontColor--secondary">
                              {heading}
                            </span>
                          </Row.Item>
                          <Row.Item>
                            <div className={activityStyles.rowContent}>
                              <span
                                className={`fontSize--m fontWeight--normal fontColor--primary ${activityStyles.ellipsisWrapperRightAligned}`}
                              >
                                {content}
                              </span>
                            </div>
                          </Row.Item>
                        </Row>
                      </div>
                    ))}
                  {groupPosting && (
                    <div className="fontSize--xs fontColor--primary margin--top--s">
                      {l10n.getString("group-posting-copy")}
                    </div>
                  )}
                  <hr className={styles.sectionDivider} />
                  <h5 className="margin--bottom--s fontSize--xs fontWeight--bold fontColor--primary">
                    {l10n.getString("label-template-details").toUpperCase()}
                  </h5>
                  {templateDetailsRows
                    .filter(([, content]) => Boolean(content))
                    .map(([heading, content], index, filteredRows) => (
                      <div
                        key={`template-row-${heading}`}
                        className={
                          index !== filteredRows.length - 1
                            ? "margin--bottom--l"
                            : ""
                        }
                      >
                        <Row alignItems="center">
                          <Row.Item shrink>
                            <span className="fontSize--s fontWeight--semibold fontColor--secondary">
                              {heading}
                            </span>
                          </Row.Item>
                          <Row.Item>
                            <div className={activityStyles.rowContent}>
                              <span
                                className={`fontSize--m fontWeight--normal fontColor--primary ${activityStyles.ellipsisWrapperRightAligned}`}
                              >
                                {content}
                              </span>
                            </div>
                          </Row.Item>
                        </Row>
                      </div>
                    ))}
                </ContentCard>
              </div>
            </Tabs.Panel>
            <Tabs.Panel tabId="version-history">
              <VersionHistoryTab
                templateUuid={templateUuid}
                groupState={formattedBulkTemplate.state}
                activeVersionId={formattedBulkTemplate.activeVersionId}
                versions={versions}
                versionsLoading={versionsLoading}
                {...(permissions.canEdit &&
                  !editLoading && {
                    onCopyToNewVersion: (
                      versionId: API.BulkTemplateVersionId
                    ) => {
                      onEdit(formattedBulkTemplate, versionId);
                    },
                  })}
              />
            </Tabs.Panel>
          </Tabs>
        </div>
      </>
    );
  };

  const archiveDisabled =
    !drawerActions.archiveEnabled || !permissions.canArchive;

  const hasSendPayments = (formattedBulkTemplate?.paymentCreditCount ?? 0) > 0;
  const hasCollectPayments =
    (formattedBulkTemplate?.paymentDebitCount ?? 0) > 0;
  const makePaymentDisabled =
    !drawerActions.makePaymentEnabled ||
    isMakePaymentDisabled(permissions, hasSendPayments, hasCollectPayments);

  return (
    <>
      <Drawer
        depth="524px"
        isOpen={isOpen}
        onUserDismiss={handleClose}
        paddingSize="xl"
        onNext={handleOnNext}
        onPrev={handleOnPrev}
        {...(formattedBulkTemplate && {
          footer: (
            <div>
              <Row alignItems="center" justifyContent="space-between">
                <Row.Item shrink>
                  <div className={styles.footerButtonGroup}>
                    <Button
                      size="xs"
                      kind="secondary"
                      startIcon="edit"
                      disabled={editDisabled}
                      onClick={() => {
                        if (formattedBulkTemplate) {
                          onEdit(formattedBulkTemplate);
                        }
                      }}
                      label={l10n.getString("button-edit")}
                    />
                    <Button
                      size="xs"
                      kind="secondary"
                      startIcon="archive"
                      disabled={archiveDisabled}
                      onClick={() => {
                        if (formattedBulkTemplate) {
                          onArchive(formattedBulkTemplate);
                        }
                      }}
                      label={l10n.getString("label-archive")}
                    />
                  </div>
                </Row.Item>
                <Row.Item shrink>
                  <Button
                    size="xs"
                    kind="primary"
                    disabled={makePaymentDisabled}
                    onClick={() => {
                      navigate(
                        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/new?templateId=${formattedBulkTemplate?.uuid}`
                      );
                    }}
                    label={l10n.getString("bulk-template-action-make-payment")}
                  />
                </Row.Item>
              </Row>
            </div>
          ),
        })}
      >
        <LoadingSkeleton
          content="paragraph"
          showTitle
          isLoading={loading && !formattedBulkTemplate}
        >
          {formattedBulkTemplate ? renderDrawerContent() : null}
        </LoadingSkeleton>
      </Drawer>
      {formattedBulkTemplate && (
        <ViewPaymentsDialog
          isOpen={isPaymentsDialogOpen}
          handleClose={() => setIsPaymentsDialogOpen(false)}
          template={formattedBulkTemplate}
          variant="template"
        />
      )}
    </>
  );
};

export default BulkTemplateDrawer;
